import { DrawingCanvasMode, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { bbox as turfBBOX } from '@turf/turf';
import { useState } from 'react';
import { MapProvider } from 'react-map-gl/mapbox';
import authorizations, { Action } from '~/authorizations';
import type { Project } from '~/common/types';
import { MapComponent } from '~/components2/Map';
import type { InitialViewState } from '~/components2/Map/common';
import { useCivilDrawMap } from '~/hooks/civilDrawV2/useCivilDrawMap';
import { useUpdateCivilDrawMapBaseMap } from '~/hooks/civilDrawV2/useUpdateCivilDrawMapBaseMap';
import ProjectDataLayers from '../map/ProjectDataLayers';
import CivilDrawDrawingCanvas from './CivilDrawDrawingCanvas';
import DrawToolButtons, { type DrawingTool } from './DrawToolButtons';

type CivilDrawV2Props = {
  project: Project;
  mapId: string;
};

/** A CivilDraw map drawn with DrawingCanvas, shown when the civildraw-use-new-drawing-canvas flag is on. */
export default function CivilDrawV2({ project, mapId }: CivilDrawV2Props) {
  const { data: map, isError } = useCivilDrawMap(project.id, mapId);
  const updateBaseMap = useUpdateCivilDrawMapBaseMap(project.id, mapId);
  const [tool, setTool] = useState<DrawingTool>({ mode: DrawingCanvasMode.EDIT });

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p role="alert" className="font-noto text-sm text-grey-80">
          This drawing couldn't be loaded. Refresh the page to try again.
        </p>
      </div>
    );
  }

  if (!map) return <SkeletonSpinnerCentered />;

  const canEdit = authorizations.can(Action.EditCivilDraw);
  const [west, south, east, north] = turfBBOX(project.geometry as unknown as GeoJSON.Feature);
  const initialViewState: InitialViewState = {
    bounds: [west, south, east, north],
    fitBoundsOptions: { padding: 100 },
  };

  return (
    // The onboarding tour highlights this element by its id
    <div id="civildraw-map" className="relative flex h-full w-full">
      <MapProvider>
        <MapComponent
          initialViewState={initialViewState}
          projectGeometry={project.geometry}
          mapStyle={map.baseMap}
          onBasemapChange={updateBaseMap}
          showLayers={true}
          showLayerPicker={true}
          showScale={true}
          units={map.unitSystem}
          showZoomButtons={true}
          showBasemapPicker={true}
          showLegend={true}
          includeDefaultContextMenu={false}
          projectId={project.id}
          currentMapId={mapId}
          id="map"
          allowRotation={true}
        >
          <ProjectDataLayers projectId={project.id} />
          <CivilDrawDrawingCanvas
            projectId={project.id}
            mapId={mapId}
            mapObjects={map.objects}
            canEdit={canEdit}
            canDelete={authorizations.can(Action.DeleteCivilDraw)}
            tool={tool}
            onToolChange={setTool}
          />
        </MapComponent>
      </MapProvider>
      {canEdit && (
        <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
          <DrawToolButtons tool={tool} onToolChange={setTool} />
        </div>
      )}
    </div>
  );
}
